import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../../components/ToastProvider';
import { FaTruck } from 'react-icons/fa';
import './Account.css'; // Ensure we reuse account styles



const OrderDetail = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const { showToast } = useToast();

    useEffect(() => {
        if (!orderId) return;

        const fetchOrder = async () => {
            try {
                const BASE_URL = process.env.REACT_APP_API_BASE || 'https://handmade-haven.in';
                const res = await fetch(`${BASE_URL}/api/customer/order-detail.php?order_id=${orderId}`, {
                    credentials: 'include'
                });

                if (res.status === 404 || res.status === 403 || res.status === 401) {
                    showToast('Order not found or unauthorized', 'error');
                    navigate('/account/orders');
                    return;
                }

                const data = await res.json();
                if (data.ok) {
                    const orderData = data.order;
                    // Fix: Ensure 'Order Placed' is always the first item in history
                    const placedEvent = {
                        status: 'Order Placed',
                        changed_at: orderData.created_at
                    };

                    // prepend if not present (simple check)
                    const hasPlaced = orderData.status_history.some(h => h.status.toLowerCase().includes('placed'));
                    if (!hasPlaced) {
                        orderData.status_history.unshift(placedEvent);
                    }

                    setOrder(orderData);
                } else {
                    showToast(data.message || 'Failed to load order', 'error');
                }
            } catch (err) {
                console.error(err);
                showToast('Network error', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, [orderId, navigate, showToast]);

    if (loading) return <div style={{ padding: '20px', color: '#666' }}>Loading order details...</div>;
    if (!order) return null;

    // Helper for timeline
    const getStatusColor = (status) => {
        const s = status.toLowerCase();
        if (s.includes('placed')) return '#718096';
        if (s.includes('pending')) return '#ecc94b';
        if (s.includes('processing')) return '#4299e1';
        if (s.includes('shipped')) return '#805ad5';
        if (s.includes('delivered')) return '#38a169';
        if (s.includes('cancelled')) return '#e53e3e';
        return '#718096';
    };

    // Helper to format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit'
        });
    };

    return (
        <div style={{ animation: 'fadeIn 0.3s ease' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', borderBottom: '1px solid #eee', paddingBottom: '20px' }}>
                <div>
                    <h2 className="account-section-title" style={{ margin: 0, border: 'none', padding: 0, fontSize: '2rem' }}>Order #{order.id}</h2>
                    <p style={{ margin: '8px 0 0', color: '#666', fontSize: '0.95rem' }}>
                        Placed on {formatDate(order.created_at)}
                    </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <span className={order.status.toLowerCase() === 'delivered' ? 'animate-pop' : ''} style={{
                        padding: '8px 16px',
                        borderRadius: '30px',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        background: `${getStatusColor(order.status)}15`,
                        color: getStatusColor(order.status),
                        border: `1px solid ${getStatusColor(order.status)}30`,
                        display: 'inline-block'
                    }}>
                        {order.status}
                    </span>
                    <div className="delivery-estimate" style={{ marginTop: '8px', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                        <FaTruck size={14} /> Estimated delivery: Ships within 1–2 days, delivered in 3–7 days
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '1.4rem', fontWeight: 'bold', color: '#333' }}>
                        ₹{order.total}
                    </div>
                </div>
            </div>

            <div className="order-detail-grid">

                {/* Left Column: Items & Address */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Items */}
                    <div className="order-card" style={{ cursor: 'default', transform: 'none', boxShadow: 'none', border: '1px solid #eee', background: '#fff' }}>
                        <h4 style={{ margin: '0 0 20px', color: '#333', borderBottom: '1px solid #eee', paddingBottom: '15px', fontSize: '1.1rem' }}>Items</h4>
                        {order.items.map((item, i) => {
                            // SINGLE SOURCE OF TRUTH: Display Logic
                            const variantLabel = item.variant_label || item.variant;
                            const displayName = variantLabel ? `${item.name} (${variantLabel})` : item.name;
                            const qty = item.quantity || item.qty || 1;

                            return (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', fontSize: '1rem' }}>
                                    <div>
                                        <span style={{ fontWeight: 600, color: '#d14c87', marginRight: '8px' }}>{qty}x</span>
                                        <span style={{ color: '#444' }}>{displayName}</span>
                                    </div>
                                    <div style={{ fontWeight: 500, color: '#333' }}>₹{item.price * qty}</div>
                                </div>
                            );
                        })}
                        <div style={{ borderTop: '1px solid #eee', paddingTop: '15px', marginTop: '10px', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                            <span>Total</span>
                            <span>₹{order.total}</span>
                        </div>
                    </div>

                    {/* Address */}
                    <div className="order-card" style={{ cursor: 'default', transform: 'none', boxShadow: 'none', border: '1px solid #eee', background: '#fff' }}>
                        <h4 style={{ margin: '0 0 20px', color: '#333', borderBottom: '1px solid #eee', paddingBottom: '15px', fontSize: '1.1rem' }}>Delivery Address</h4>
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                            <div style={{ background: '#fff0f5', padding: '10px', borderRadius: '50%', color: '#d14c87' }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                            </div>
                            <div>
                                <p style={{ whiteSpace: 'pre-line', margin: '0 0 10px', lineHeight: 1.6, color: '#555', fontSize: '0.95rem' }}>
                                    {order.address}
                                </p>
                                <div style={{ fontSize: '0.9rem', color: '#666', background: '#f8f8f8', padding: '8px 12px', borderRadius: '6px', display: 'inline-block' }}>
                                    <strong>Contact:</strong> {order.phone}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Timeline */}
                <div className="order-card" style={{ cursor: 'default', transform: 'none', boxShadow: 'none', border: '1px solid #ffdde7', background: '#fff9fa' }}>
                    <h4 style={{ margin: '0 0 25px', color: '#d14c87', fontSize: '1.1rem' }}>Order Timeline</h4>
                    <div style={{ position: 'relative', borderLeft: '2px solid #ffb6c9', paddingLeft: '25px', marginLeft: '10px' }}>
                        {order.status_history && order.status_history.map((hist, idx) => (
                            <div key={idx} style={{ position: 'relative', marginBottom: '30px', lastChild: { marginBottom: 0 } }}>
                                {/* Dot */}
                                <div style={{
                                    position: 'absolute',
                                    left: '-31px',
                                    top: '4px',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    background: getStatusColor(hist.status),
                                    boxShadow: `0 0 0 4px #fff`
                                }}></div>

                                <div style={{ fontWeight: 600, textTransform: 'capitalize', color: '#333', fontSize: '1rem', marginBottom: '4px' }}>
                                    {hist.status}
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#777', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                    {formatDate(hist.changed_at)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>

            <button
                onClick={() => navigate('/account/orders')}
                style={{
                    marginTop: '30px',
                    background: 'white',
                    border: '1px solid #eee',
                    color: '#555',
                    cursor: 'pointer',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: 500,
                    transition: 'all 0.2s'
                }}
                onMouseOver={(e) => { e.target.style.borderColor = '#d14c87'; e.target.style.color = '#d14c87'; }}
                onMouseOut={(e) => { e.target.style.borderColor = '#eee'; e.target.style.color = '#555'; }}
            >
                <span>&larr;</span> Back to Orders
            </button>
        </div>
    );
};

export default OrderDetail;
